import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { convertDomainArrayToMap, getDomainData } from '../../library/Core/SmartFunctions';
import FactLoader from '../../library/SimpleControls/FactLoader';
import * as storyApi from './StoryService';
import StoryPreviewCard from './StoryPreviewCard';

function labelFor(domain: Map<string, any[]> | null, category: string, code: string | null | undefined) {
    if (!code) return '';
    const match = domain?.get(category)?.find((item) => item.code === code);
    return match?.value || code;
}

function when(value: string | null | undefined) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function yesNo(value: boolean | null | undefined) {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
}

const StoryView = () => {
    const { storyId } = useParams();
    const location = useLocation();
    const [story, setStory] = useState<any>(null);
    const [status, setStatus] = useState<number | null>(null);
    const [domain, setDomain] = useState<Map<string, any[]> | null>(null);
    const [activeTab, setActiveTab] = useState<'log' | 'markers'>('log');
    const queueFailed = (location.state as any)?.queueStatus === 'failed';

    useEffect(() => {
        let cancelled = false;
        storyApi.get(storyId as string).then((response) => {
            if (!cancelled) setStory(response.data);
        }).catch((err) => {
            if (!cancelled) setStatus(err?.response?.status || 500);
        });
        getDomainData().then((rows) => {
            if (!cancelled && Array.isArray(rows)) setDomain(convertDomainArrayToMap(rows));
        }).catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    if (status === 403) return <div className="alert alert-warning m-4">You do not have access to this story.</div>;
    if (status === 404 || status) return <div className="alert alert-warning m-4">This story could not be found.</div>;
    if (!story) return <FactLoader show />;

    const education = labelFor(domain, 'PRESENT_EDUCATION_STATUS_CODE', story.presentEducationStatusCode);
    const working = labelFor(domain, 'WORK_STATUS_CODE', story.workingStatusCode);
    const completion = story.profileCompletionPercentage === null || story.profileCompletionPercentage === undefined || story.profileCompletionPercentage === ''
        ? ''
        : `${story.profileCompletionPercentage}%`;

    return (
        <div className="container-fluid py-5 bg-white position-relative z1">
           

            {queueFailed && <div className="alert alert-warning">The story was saved and the email was not queued.</div>}
            {/* <h1 className="h3">Story</h1>
            <p>
                <strong>Institute:</strong> {story.institute?.name}
            </p>
            <p>
                <strong>Narrator:</strong> {story.narrator?.name} {story.narrator?.passoutYear ? `(${story.narrator.passoutYear})` : ''}
            </p>
            <p>
                <strong>Bio:</strong> {story.narrator?.professionalHeadline || '—'}
            </p>
            <p>
                <strong>Protagonist:</strong> {story.protagonist?.name} {story.protagonist?.email ? `(${story.protagonist.email})` : ''}
            </p>
            <p>
                <strong>Spouse:</strong> {story.spouse ? `${story.spouse.name || ''} ${story.spouse.email || ''}` : '—'}
            </p>
            <p>
                <strong>Program:</strong> {story.program?.name || '—'}
            </p>
            <p>
                <strong>Categories:</strong> {(story.categories || []).map((item: any) => item.label || item.code).join(', ') || '—'}
            </p>
            <p>
                <strong>Why noteworthy:</strong> {story.whyNoteworthy}
            </p>
            <div className="mb-3" dangerouslySetInnerHTML={{ __html: story.narrativeHtml || '' }} />
            <p>
                <strong>LinkedIn:</strong> {story.linkedin?.available ? 'Yes' : 'No'}{' '}
                {story.linkedin?.info ? `— ${story.linkedin.info}` : ''}
            </p>
            <p>
                <strong>Google first 3 pages:</strong> {yesNo(story.googleTop3)}
            </p>
            <p>
                <strong>Social tag:</strong> {yesNo(story.socialTagged)}
            </p>
            <p>
                <strong>Website recognition:</strong> {yesNo(story.websiteRecognized)}
            </p>
            <p>
                <strong>Collateral:</strong>
            </p>
            <ul>
                {(story.files || []).map((file: any) => (
                    <li key={file.url}>
                        <a href={file.url} target="_blank" rel="noreferrer">
                            {file.name || file.url}
                        </a>
                    </li>
                ))}
            </ul>
            <p>
                <strong>Markers:</strong> {(story.markers || []).map((marker: any) => marker.label || marker.markerField).join(', ') || '—'}
            </p>
            <p>
                <strong>Rating:</strong> {story.rating || '—'}
            </p>
            <p>
                <strong>Share with Univariety:</strong> {story.shareUnivariety ? 'Yes' : 'No'} {story.univarietyEmails || ''}
            </p>
            <p>
                <strong>Share with institute:</strong> {story.shareInstitute ? 'Yes' : 'No'}
            </p>
            <p>
                <strong>Reporter:</strong> {story.reporter?.name || '—'}
            </p>
            <p>
                <strong>Mapped outreach:</strong>{' '}
                {story.outreach ? `${when(story.outreach.createdAt)} ${story.outreach.team || ''} ${story.outreach.duration || ''}` : '—'}
            </p>
            <p>
                <strong>Present status:</strong> {[education, working, completion].filter(Boolean).join(' · ') || '—'}
            </p>
            <p>
                <strong>Created:</strong> {when(story.createdAt)}
            </p>
            <p>
                <strong>Updated:</strong> {when(story.updatedAt)}
            </p> */}

            <div className='max-1140 mx-auto'>
                    <StoryPreviewCard
                        whyNoteworthy={story.whyNoteworthy}
                        narrativeHtml={story.narrativeHtml}
                        narrator={{
                            name: story.narrator?.name,
                            firstName: story.narrator?.firstName,
                            lastName: story.narrator?.lastName,
                            programName: story.narrator?.programName,
                            passoutYear: story.narrator?.passoutYear,
                            profilePictureFilePath: story.narrator?.profilePictureFilePath,
                            genderCode: story.narrator?.genderCode,
                        }}
                        protagonist={{
                            name: story.protagonist?.name,
                            firstName: story.protagonist?.firstName,
                            lastName: story.protagonist?.lastName,
                            programName: story.program?.name,
                            passoutYear: story.protagonist?.passoutYear,
                            profilePictureFilePath: story.protagonist?.profilePictureFilePath,
                            genderCode: story.protagonist?.genderCode,
                        }}
                        categories={(story.categories || []).map((item: any) => item.label || item.code)}
                        markers={(story.markers || []).map((marker: any) => marker.label || marker.markerField)}
                        rating={story.rating}
                        files={story.files || []}
                        editHref={`/support/story/${storyId}/edit`}
                    />
                </div>

        </div>
    );
};

export default StoryView;
